<script setup>

// Imports
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { getSwiper } from '@/utils/swiper-loader'

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

// Mobile: Swiper carousel with autoplay. Desktop: a row of cards where one is expanded (no Swiper),
// advanced by our own timer with the same delay and the same progress pagination.
const MOBILE_QUERY = '(max-width: 1023px)'
const AUTOPLAY_DELAY = 5000

const cards = computed(() => (Array.isArray(props.block?.cards) ? props.block.cards : []))

const sectionRef = ref(null)
const sliderRef = ref(null)
const paginationRef = ref(null)
const desktopPaginationRef = ref(null)
const cardEls = []
const activeIndex = ref(0)

let swiperInstance = null
let initPromise = null
let mediaQuery = null

const activate = (index) => {
  activeIndex.value = index
}

const clamp01 = (value) => String(Math.min(Math.max(value, 0), 1))

const setProgress = (value) => {
  paginationRef.value?.style.setProperty('--progress', clamp01(value))
}

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

/*
  Desktop autoplay: a requestAnimationFrame timer that starts counting once the section has been
  in view (see VISIBLE_SHARE; rAF itself stops in background tabs). A manual switch restarts the count.
*/
let desktopRaf = null
let desktopLastTs = null
let desktopElapsed = 0
// Autoplay (both layouts) starts the first time more than 2/3 of the section is visible,
// then keeps running regardless of scroll
const VISIBLE_SHARE = 2 / 3
let autoplayUnlocked = false
let sectionObserver = null

const setDesktopProgress = (value) => {
  desktopPaginationRef.value?.style.setProperty('--progress', clamp01(value))
}

const resetDesktopTimer = () => {
  desktopElapsed = 0
  desktopLastTs = null
  setDesktopProgress(0)
}

const desktopTick = (timestamp) => {
  desktopRaf = requestAnimationFrame(desktopTick)

  // Cap the step so a long frame gap (tab switch, breakpoint change) does not skip a slide
  const step = desktopLastTs === null ? 0 : Math.min(timestamp - desktopLastTs, 100)
  desktopLastTs = timestamp
  if (!autoplayUnlocked) return

  desktopElapsed += step
  if (desktopElapsed >= AUTOPLAY_DELAY) {
    activate((activeIndex.value + 1) % cards.value.length)
    desktopElapsed = 0
  }
  setDesktopProgress(desktopElapsed / AUTOPLAY_DELAY)
}

const startDesktopAutoplay = () => {
  if (desktopRaf || cards.value.length < 2 || prefersReducedMotion()) return
  resetDesktopTimer()
  desktopRaf = requestAnimationFrame(desktopTick)
}

const stopDesktopAutoplay = () => {
  if (desktopRaf) cancelAnimationFrame(desktopRaf)
  desktopRaf = null
}

// Manual switch (card click, bullet, keyboard): same on both layouts, restarts the timer
const select = (index) => {
  if (swiperInstance) {
    // Tapping the current slide must not restart the timer: any slideTo, even to the same slide,
    // fires beforeTransitionStart, on which Swiper's autoplay resets its delay
    if (index !== swiperInstance.realIndex) swiperInstance.slideToLoop(index)
    return
  }
  // Desktop: likewise, only a change of card restarts the timer
  if (index === activeIndex.value) return
  activate(index)
  resetDesktopTimer()
}

const focusCard = (index) => {
  // preventScroll: focusing a slide must not scroll the overflow: hidden slider under Swiper
  cardEls[index]?.focus({ preventScroll: true })
}

const onCardKeydown = (event, index) => {
  // Keys pressed inside the card's link are the link's own
  if (event.target !== event.currentTarget) return

  const count = cards.value.length
  let target = null

  switch (event.key) {
    case 'Enter':
    case ' ':
      target = index
      break
    case 'ArrowRight':
      target = (index + 1) % count
      break
    case 'ArrowLeft':
      target = (index - 1 + count) % count
      break
    case 'Home':
      target = 0
      break
    case 'End':
      target = count - 1
      break
    default:
      return
  }

  event.preventDefault()
  select(target)
  if (target !== index) focusCard(target)
}

const initSwiper = async () => {
  if (swiperInstance || initPromise || !sliderRef.value || cards.value.length < 2) return

  initPromise = (async () => {
    const { Swiper, modules } = await getSwiper(['Pagination', 'Autoplay'])
    // The viewport may have grown past the breakpoint while Swiper was loading
    if (!sliderRef.value || !mediaQuery?.matches) return

    const reducedMotion = prefersReducedMotion()

    swiperInstance = new Swiper(sliderRef.value, {
      slidesPerView: 1,
      spaceBetween: 16,
      speed: 450,
      // Swiper needs at least 3 slides to loop with one slide per view
      loop: cards.value.length > 2,
      initialSlide: activeIndex.value,
      // Keeps running after a swipe; Swiper pauses it while a finger holds the slide or the tab is hidden
      autoplay: reducedMotion
        ? false
        : {
            delay: AUTOPLAY_DELAY,
            disableOnInteraction: false
          },
      pagination: paginationRef.value
        ? {
            el: paginationRef.value,
            clickable: true,
            renderBullet: (index, className) =>
              `<button type="button" class="${className}" aria-label="Go to slide ${index + 1} of ${cards.value.length}"></button>`
          }
        : undefined,
      modules,
      on: {
        // realIndex: in loop mode activeIndex counts the reordered slides, not the cards
        slideChange: (swiper) => {
          activate(swiper.realIndex)
          setProgress(0)
        },
        // The active bullet fills with the time already spent on the slide, in sync with the timer
        autoplayTimeLeft: (swiper, timeLeft, percentage) => setProgress(1 - percentage)
      }
    })

    // Swiper starts autoplay on init; hold it until the section has been in view
    syncMobileAutoplay()
  })()

  await initPromise
  initPromise = null
}

/*
  Mobile: Swiper starts autoplay on init, so it is stopped until the section has been in view, then
  started once. stop() rather than pause(): Swiper's pause() resumes itself on the next transitionend.
*/
const syncMobileAutoplay = () => {
  const autoplay = swiperInstance?.params?.autoplay?.enabled ? swiperInstance.autoplay : null
  if (!autoplay) return

  if (autoplayUnlocked && !autoplay.running) {
    autoplay.start()
  } else if (!autoplayUnlocked && autoplay.running) {
    autoplay.stop()
    setProgress(0)
  }
}

const destroySwiper = () => {
  swiperInstance?.destroy?.(true, true)
  swiperInstance = null
}

const syncMode = () => {
  if (mediaQuery?.matches) {
    stopDesktopAutoplay()
    initSwiper()
  } else {
    destroySwiper()
    startDesktopAutoplay()
  }
}

onMounted(() => {
  if (typeof window === 'undefined' || !('matchMedia' in window)) return

  if (sectionRef.value && 'IntersectionObserver' in window) {
    sectionObserver = new IntersectionObserver(([entry]) => {
      // A section taller than the viewport can never be 2/3 visible; then 2/3 of the viewport counts
      const viewportHeight = entry.rootBounds?.height || window.innerHeight
      const inView = entry.isIntersecting && (
        entry.intersectionRatio >= VISIBLE_SHARE
        || entry.intersectionRect.height >= viewportHeight * VISIBLE_SHARE
      )
      if (!inView) return

      // One-shot: once started, autoplay no longer depends on scroll
      autoplayUnlocked = true
      sectionObserver?.disconnect()
      sectionObserver = null
      resetDesktopTimer()
      syncMobileAutoplay()
    }, {
      threshold: [0, 0.25, 0.5, VISIBLE_SHARE, 0.75, 1]
    })
    sectionObserver.observe(sectionRef.value)
  } else {
    autoplayUnlocked = true
  }

  mediaQuery = window.matchMedia(MOBILE_QUERY)
  mediaQuery.addEventListener('change', syncMode)
  syncMode()
})

onBeforeUnmount(() => {
  mediaQuery?.removeEventListener('change', syncMode)
  mediaQuery = null
  sectionObserver?.disconnect()
  sectionObserver = null
  stopDesktopAutoplay()
  destroySwiper()
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section ref="sectionRef"
  class="use-cases-slider section-mb">
  <div class="container">
    <div v-if="block.headingAdvanced?.html"
      v-html="block.headingAdvanced?.html"
      class="use-cases-slider__heading"></div>

    <div v-if="cards.length"
      ref="sliderRef"
      class="use-cases-slider__slider swiper">
      <div class="use-cases-slider__track swiper-wrapper">

        <!--
          A click, Enter / Space on the focused card, or ← → / Home / End switch cards;
          on desktop the active card expands
        -->
        <article v-for="(card, index) in cards"
          :key="card.id"
          :ref="(el) => { cardEls[index] = el }"
          class="use-cases-slider__card swiper-slide"
          :class="{ 'is-active': index === activeIndex }"
          tabindex="0"
          @click="select(index)"
          @keydown="onCardKeydown($event, index)">

          <div class="use-cases-slider__card-image">
            <GlobalsImage
              :landscape="card.imageDesktop"
              :portrait="card.imageMobile"
              :max-width="1600"
              :aria-hidden="true"
            />
          </div>

          <div class="use-cases-slider__card-body">
            <div class="use-cases-slider__card-header">
              <div v-if="card.iconField?.length"
                class="use-cases-slider__card-icon">
                <img v-if="card.iconField.length"
                  :src="card.iconField[0].url"
                  width="32" height="32"
                  alt="" aria-hidden="true" loading="lazy">
              </div>

              <p v-if="card.iconTitle"
                class="use-cases-slider__card-title">
                {{ card.iconTitle }}
              </p>
            </div>

            <div class="use-cases-slider__card-content">
              <div v-if="card.headingAdvanced?.html"
                v-html="card.headingAdvanced?.html"
                class="use-cases-slider__card-heading"></div>

              <a v-if="card.linkTitle?.url"
                :href="card.linkTitle.url"
                class="use-cases-slider__card-link text-inline-link">
                {{ card.linkTitle.title }}
              </a>
            </div>
          </div>
        </article>

      </div>
    </div>

    <!-- Mobile: bullets rendered by Swiper -->
    <div ref="paginationRef"
      class="use-cases-slider__pagination"></div>

    <!-- Desktop: the same bullets, rendered here and driven by the desktop timer -->
    <div v-if="cards.length > 1"
      ref="desktopPaginationRef"
      class="use-cases-slider__pagination use-cases-slider__pagination--desktop">
      <button v-for="(card, index) in cards"
        :key="card.id"
        type="button"
        class="swiper-pagination-bullet"
        :class="{ 'swiper-pagination-bullet-active': index === activeIndex }"
        :aria-label="`Go to slide ${index + 1} of ${cards.length}`"
        :aria-current="index === activeIndex ? 'true' : undefined"
        @click="select(index)"></button>
    </div>
  </div>
</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.use-cases-slider__heading {
  margin-bottom: 32px;
  text-align: center;

  @media (min-width: 1024px) {
    margin-bottom: 48px;
  }
}

/* Minimal Swiper layout (the package CSS is not loaded) */
.use-cases-slider__slider {
  overflow: hidden;

  @media (min-width: 1024px) {
    overflow: visible;
  }
}

.use-cases-slider__track {
  display: flex;
}

/* Card — mobile: a full slide with everything visible */
.use-cases-slider__card {
  position: relative;
  flex-shrink: 0;
  width: 100%;
  aspect-ratio: 344 / 410;
  overflow: hidden;
  border-radius: 16px;
  background-color: #f7f5f0;
  color: #fff;
  isolation: isolate;
  outline: none;
}

.use-cases-slider__card:focus-visible {
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #292016;
}

.use-cases-slider__card-image {
  position: absolute;
  inset: 0;
  z-index: -1;
}

.use-cases-slider__card-image :deep(picture),
.use-cases-slider__card-image :deep(img) {
  display: block;
  width: 100%;
  height: 100%;
}

.use-cases-slider__card-image :deep(img) {
  object-fit: cover;
  object-position: center;
}

.use-cases-slider__card-body {
  display: flex;
  flex-direction: column;
  gap: 13px;
  padding: 28px;
}

.use-cases-slider__card-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.use-cases-slider__card-icon {
  flex: none;
  display: grid;
  place-items: center;
  width: 31px;
  height: 31px;
  border-radius: 7px;
  background: rgba(255, 255, 255, .22);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}

.use-cases-slider__card-icon img {
  display: block;
  width: 19px;
  height: 19px;
  object-fit: contain;
}

.use-cases-slider__card-title,
.use-cases-slider__card-heading,
.use-cases-slider__card-link {
  margin: 0;
  color: #fff;
  font-family: var(--font-family-primary);
  font-weight: 500;
  letter-spacing: -.02em;
}

.use-cases-slider__card-title {
  font-size: 16px;
  line-height: 22px;
}

.use-cases-slider__card-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 17px;
}

.use-cases-slider__card-heading {
  font-size: 20px;
  line-height: 26px;
}

.use-cases-slider__card-heading :deep(*) {
  margin: 0;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
}

.use-cases-slider__card-link {
  font-size: 16px;
  line-height: 22px;
}

/* Pagination pill (mobile) */
.use-cases-slider__pagination {
  /* Share of the slide's autoplay time already spent (0–1), set from Swiper's autoplayTimeLeft */
  --progress: 0;

  display: flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  margin: 24px auto 0;
  padding: 16px 20px;
  border-radius: 50px;
  background-color: #f7f5f0;

  &:empty {
    display: none;
  }

  @media (min-width: 1024px) {
    display: none;
  }
}

.use-cases-slider__pagination.use-cases-slider__pagination--desktop {
  display: none;

  @media (min-width: 1024px) {
    display: flex;
    margin-top: 24px;
  }
}

.use-cases-slider__pagination :deep(.swiper-pagination-bullet) {
  display: block;
  width: 8px;
  height: 8px;
  padding: 0;
  border: 0;
  border-radius: 50px;
  background-color: var(--color-gray-250);
  cursor: pointer;
  transition: width .3s cubic-bezier(.4, 0, .2, 1), background-color .3s;
}

.use-cases-slider__pagination :deep(.swiper-pagination-bullet) {
  position: relative;
  overflow: hidden;
}

/* Autoplay progress: the active bullet fills with black as the slide's time runs out */
.use-cases-slider__pagination :deep(.swiper-pagination-bullet::after) {
  content: "";
  position: absolute;
  inset: 0;
  background-color: #000;
  transform: scaleX(0);
  transform-origin: left center;
}

.use-cases-slider__pagination :deep(.swiper-pagination-bullet-active) {
  width: 64px;
}

.use-cases-slider__pagination :deep(.swiper-pagination-bullet-active::after) {
  /* No var() fallback: postcss-custom-properties (preserve: false) would inline it and drop the var */
  transform: scaleX(var(--progress));
}

.use-cases-slider__pagination :deep(.swiper-pagination-bullet:focus-visible) {
  outline: 2px solid #000;
  outline-offset: 2px;
}

/*
  Desktop: all cards in one row, the active one expanded. Widths keep the design's 760 : 186
  ratio at any width; the row height keeps the expanded card at the design's 760 × 466.
*/
@media (min-width: 1024px) {
  .use-cases-slider__slider {
    --gap: 12px;
    --expanded-width: calc((100cqi - 4 * var(--gap)) * 760 / 1504);
    container-type: inline-size;
  }

  .use-cases-slider__track {
    gap: var(--gap);
    height: calc(var(--expanded-width) * 466 / 760);
  }

  .use-cases-slider__card {
    flex: 186 1 0;
    width: auto;
    min-width: 0;
    aspect-ratio: auto;
    cursor: pointer;
    transition: flex-grow .5s cubic-bezier(.4, 0, .2, 1);
  }

  .use-cases-slider__card.is-active {
    flex-grow: 760;
    cursor: default;
  }

  /*
    The image is always as wide as an expanded card, so collapsing crops, never rescales.
    Collapsed: shows the strip starting 17% into the image. Expanded: centred (animated together
    with the card width).
    --expanded-width assumes five cards; with fewer, the open card grows wider than that, so the
    image takes the card's own width instead and never leaves empty edges.
  */
  .use-cases-slider__card-image :deep(picture) {
    position: absolute;
    top: 0;
    left: 0;
    width: max(var(--expanded-width), 100%);
    transform: translateX(-17%);
    transition: left .5s cubic-bezier(.4, 0, .2, 1), transform .5s cubic-bezier(.4, 0, .2, 1);
  }

  .use-cases-slider__card.is-active .use-cases-slider__card-image :deep(picture) {
    left: 50%;
    transform: translateX(-50%);
  }

  /* The body keeps the expanded width too, so the text never reflows while animating */
  .use-cases-slider__card-body {
    gap: 16px;
    width: var(--expanded-width);
    padding: 24px;
  }

  .use-cases-slider__card-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    -webkit-backdrop-filter: blur(17px);
    backdrop-filter: blur(17px);
  }

  .use-cases-slider__card-icon img {
    width: 25px;
    height: 25px;
  }

  .use-cases-slider__card-header {
    gap: 12px;
  }

  .use-cases-slider__card-title {
    font-size: 18px;
    line-height: 26px;
  }

  .use-cases-slider__card-content {
    gap: 20px;
  }

  .use-cases-slider__card-heading {
    max-width: 567px;
    font-size: 24px;
    line-height: 32px;
  }

  .use-cases-slider__card-link {
    font-size: 16px;
    line-height: 22px;
  }

  /* Collapsed: only the icon stays */
  .use-cases-slider__card-title,
  .use-cases-slider__card-content {
    opacity: 0;
    visibility: hidden;
    transition: opacity .2s, visibility 0s .2s;
  }

  .use-cases-slider__card.is-active .use-cases-slider__card-title,
  .use-cases-slider__card.is-active .use-cases-slider__card-content {
    opacity: 1;
    visibility: visible;
    transition: opacity .3s .2s, visibility 0s .2s;
  }
}

@media (min-width: 1440px) {
  .use-cases-slider__card-body {
    gap: 22px;
    padding: 36px;
  }

  .use-cases-slider__card-icon {
    width: 52px;
    height: 52px;
    border-radius: 12px;
  }

  .use-cases-slider__card-icon img {
    width: 32px;
    height: 32px;
  }

  .use-cases-slider__card-header {
    gap: 16px;
  }

  .use-cases-slider__card-title {
    font-size: 22px;
    line-height: 32px;
  }

  .use-cases-slider__card-content {
    gap: 28px;
  }

  .use-cases-slider__card-heading {
    font-size: 30px;
    line-height: 40px;
  }

  .use-cases-slider__card-link {
    font-size: 18px;
    line-height: 25px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .use-cases-slider__card,
  .use-cases-slider__card-image :deep(picture),
  .use-cases-slider__card-title,
  .use-cases-slider__card-content,
  .use-cases-slider__pagination :deep(.swiper-pagination-bullet) {
    transition: none !important;
  }

  /* No autoplay: the active bullet is simply filled */
  .use-cases-slider__pagination :deep(.swiper-pagination-bullet-active::after) {
    transform: scaleX(1);
  }
}

</style>
